/* eslint-disable max-statements */
import { Alert, Button, ButtonGroup } from "react-bootstrap";
import { DesktopComputerIcon, PlusIcon, TrashIcon, UserIcon } from "@heroicons/react/outline";
import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";


import { isCourseInFuture, isHybridEvent, registrationIsOpen } from "../helpers/event";
import RegistrationClosedMessage from "../components/RegistrationClosedMessage";
import { useAuth } from "../services/wp.auth.service";
import { useCourseRegistration } from "../services/CourseRegistrationProvider";
import { userCanRegister } from "../helpers/authHelpers";

const CourseRegisterButton = ({
	className,
	iconClassName,
	course,
	groupClassName,
	message,
	showClosedMessage,
	variant
}) => {
	const { user } = useAuth();
	const canRegister = useMemo(() => userCanRegister(user), [user]);

	if (!canRegister) {
		return null;
	}

	const { addCourse, cancelRegistration, registeredCourses, registeredFor, removeCourse } = useCourseRegistration();

	const addCourseToRegistration = useCallback(() => {
		addCourse(course);
	}, []);

	const addOnlineCourseToRegistration = useCallback(() => {
		addCourse({
			...course,
			online: true
		});
	}, []);

	const removeCourseFromRegistration = useCallback(() => {
		removeCourse(course);
	}, []);

	const cancelEventRegistration = useCallback(() => {
		cancelRegistration(course);
	}, []);

	const registered = useMemo(() => registeredFor(course), [registeredCourses]);
	const isSeries = useMemo(() => course.series?.courses?.filter(isCourseInFuture).length || false, [course.id]);
	const iconClasses = useMemo(() => `i me-2 ${iconClassName}`, [iconClassName]);

	if (user && user?.registrations[course.id]) {
		return (
			<Button className={className} onClick={cancelEventRegistration} variant={variant}>
				<TrashIcon className={iconClasses} />
				Cancel Registration
			</Button>);
	}

	if (!registrationIsOpen(course)) {
		return showClosedMessage
			? <RegistrationClosedMessage className={`text-center ${className}`} course={course} />
			: null;
	}

	return registered
		? (
			<Button className={className} onClick={removeCourseFromRegistration} variant={variant}>
				<TrashIcon className={iconClasses} />
				{isSeries ? "Remove Full Series" : "Remove from Registration"}
			</Button>
		)
		: isHybridEvent(course)
			? (
				<>
					<p>{message || "Please choose how you would like to attend:"}</p>
					{ course.availableSeats <= 0 ? <Alert variant="danger">No in-person seats available</Alert> : null }
					{ course.availableOnlineSeats <= 0 ? <Alert variant="danger">No online seats available</Alert> : null }
					<ButtonGroup className={groupClassName}>
						<Button className={`p-1 py-3 ${className}`} disabled={course.availableSeats <= 0} onClick={addCourseToRegistration} variant={variant}>
							<UserIcon className={iconClasses} />
							In-Person
						</Button>
						<Button className={`p-1 ${className}`} disabled={course.availableOnlineSeats <= 0} onClick={addOnlineCourseToRegistration} variant={variant}>
							<DesktopComputerIcon className={iconClasses} />
							Online
						</Button>
					</ButtonGroup>
				</>
			)
			: (
				<Button className={className} onClick={addCourseToRegistration} variant={variant}>
					<PlusIcon className={iconClasses} />
					{isSeries ? `Add Full Series (${isSeries} Classes)` : "Add Class to Registration"}
				</Button>
			);
};

CourseRegisterButton.propTypes = {
	className: PropTypes.string,
	course: PropTypes.shape({
		availableOnlineSeats: PropTypes.number,
		availableSeats: PropTypes.number,
		id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		series: PropTypes.shape({
			courses: PropTypes.arrayOf(PropTypes.shape({}))
		})
	}).isRequired,
	groupClassName: PropTypes.string,
	iconClassName: PropTypes.string,
	message: PropTypes.string,
	showClosedMessage: PropTypes.bool,
	variant: PropTypes.string
};

CourseRegisterButton.defaultProps = {
	className: "mb-4 px-2 fs-14 w-100",
	groupClassName: "w-100",
	iconClassName: "",
	message: "",
	showClosedMessage: true,
	variant: "primary"
};

export default CourseRegisterButton;
